import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Map, Source, Layer, NavigationControl, FullscreenControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
  Alert,
  Snackbar,
  Fab,
  Drawer,
  AppBar,
  Toolbar,
  Autocomplete,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Map as MapIcon,
  Close as CloseIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Satellite as SatelliteIcon,
  MapOutlined as MapOutlinedIcon,
  Terrain as TerrainIcon2,
  NightsStay as NightsStayIcon,
  WbSunny as WbSunnyIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Label as LabelIcon,
  LabelOff as LabelOffIcon,
  ViewInAr as ViewInArIcon,
  ViewModule as ViewModuleIcon,
  CameraAlt as CameraAltIcon,
} from '@mui/icons-material';
import { MAP_BOX_API_KEY } from '../../services/AppConstant';
import { addMap, getMaps, getMapById, updateMap, deleteMap } from '../../api';
import { useGlobalContext } from '../../GlobalProvider';
// Set Mapbox access token
mapboxgl.accessToken = MAP_BOX_API_KEY;

// Map style options
const mapStyles = [
  {
    id: 'satellite',
    name: 'Satellite',
    url: 'mapbox://styles/mapbox/satellite-v9',
    icon: <SatelliteIcon />,
    description: 'High-resolution satellite imagery'
  },
  {
    id: 'satellite-streets',
    name: 'Satellite Streets',
    url: 'mapbox://styles/mapbox/satellite-streets-v12',
    icon: <MapOutlinedIcon />,
    description: 'Satellite with street labels'
  },
  {
    id: 'streets',
    name: 'Streets',
    url: 'mapbox://styles/mapbox/streets-v12',
    icon: <MapIcon />,
    description: 'Standard street map'
  },
  {
    id: 'outdoors',
    name: 'Outdoors',
    url: 'mapbox://styles/mapbox/outdoors-v12',
    icon: <TerrainIcon2 />,
    description: 'Outdoor recreation map'
  },
  {
    id: 'light',
    name: 'Light',
    url: 'mapbox://styles/mapbox/light-v11',
    icon: <WbSunnyIcon />,
    description: 'Light theme street map'
  },
  {
    id: 'dark',
    name: 'Dark',
    url: 'mapbox://styles/mapbox/dark-v11',
    icon: <NightsStayIcon />,
    description: 'Dark theme street map'
  },
  {
    id: 'navigation',
    name: 'Navigation',
    url: 'mapbox://styles/mapbox/navigation-day-v1',
    icon: <MapIcon />,
    description: 'Navigation optimized map'
  },
  {
    id: 'navigation-night',
    name: 'Navigation Night',
    url: 'mapbox://styles/mapbox/navigation-night-v1',
    icon: <NightsStayIcon />,
    description: 'Night navigation map'
  }
];

const MapEditor = () => {
  const { fullApp } = useGlobalContext();
  const [viewport, setViewport] = useState({
    latitude: 30.2672,
    longitude: -97.7431,
    zoom: 18,
  });

  // Update viewport when fullApp is loaded
  useEffect(() => {
    if (fullApp?.geolocation) {
      setViewport(prev => ({
        ...prev,
        latitude: fullApp.geolocation.lat,
        longitude: fullApp.geolocation.lng,
      }));
    }
  }, [fullApp]);

  const [mapRef, setMapRef] = useState(null);
  const [draw, setDraw] = useState(null);
  const [polygons, setPolygons] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [editingFeature, setEditingFeature] = useState(null);
  const [editingProperties, setEditingProperties] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mapName, setMapName] = useState('');
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');

  // Map style state
  const [currentMapStyle, setCurrentMapStyle] = useState('satellite');

  const [mapList, setMapList] = useState([]);
  const [selectedMap, setSelectedMap] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [mapToDelete, setMapToDelete] = useState(null);
  const [showLabels, setShowLabels] = useState(true);
  const [is3DMode, setIs3DMode] = useState(true);

  // Toggle 3D mode with proper camera positioning
  const toggle3DMode = () => {
    const new3DMode = !is3DMode;
    setIs3DMode(new3DMode);
    
    // Debug: Log polygon data when switching to 3D mode
    if (new3DMode && polygons.length > 0) {
      console.log('3D Mode - Polygons data:', polygons);
      console.log('3D Mode - First polygon properties:', polygons[0]?.properties);
    }
    
    // Adjust camera for 3D view
    setViewport(prev => ({
      ...prev,
      pitch: new3DMode ? 60 : 0,
      bearing: 0,
      transitionDuration: 1000,
    }));
  };

  // Reset camera to default position
  const resetCamera = () => {
    setViewport(prev => ({
      ...prev,
      latitude: fullApp?.geolocation?.lat || 30.2672,
      longitude: fullApp?.geolocation?.lng || -97.7431,
      zoom: 18,
      pitch: is3DMode ? 60 : 0,
      bearing: 0,
      transitionDuration: 1000,
    }));
  };

  // Point-in-polygon algorithm (Ray casting algorithm)
  const isPointInPolygon = (point, polygon) => {
    const [x, y] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    return inside;
  };

  // Calculate center point of a polygon
  const calculatePolygonCenter = (coordinates) => {
    if (!coordinates || coordinates.length === 0) return null;
    
    const outerRing = coordinates[0]; // Get the outer ring
    let sumX = 0, sumY = 0;
    
    for (const coord of outerRing) {
      sumX += coord[0];
      sumY += coord[1];
    }
    
    return [sumX / outerRing.length, sumY / outerRing.length];
  };

  // Generate label features from polygons
  const generateLabelFeatures = () => {
    return polygons.map(polygon => {
      const center = calculatePolygonCenter(polygon.geometry.coordinates);
      if (!center) return null;
      
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: center,
        },
        properties: {
          name: polygon.properties.name || 'Building',
          id: polygon.id,
        },
      };
    }).filter(Boolean); // Remove null entries
  };

  // Get current map style URL
  const getCurrentMapStyle = () => {
    const style = mapStyles.find(s => s.id === currentMapStyle);
    return style ? style.url : mapStyles[0].url;
  };

  // Geocoding function
  const searchAddress = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAP_BOX_API_KEY}&types=poi,address,neighborhood,place&limit=5`
      );
      const data = await response.json();

      if (data.features) {
        setSearchResults(data.features.map(feature => ({
          id: feature.id,
          name: feature.place_name,
          coordinates: feature.center,
          type: feature.place_type[0],
        })));
      }
    } catch (error) {
      console.error('Error searching address:', error);
      setSnackbar({
        open: true,
        message: 'Error searching address. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Navigate to selected location
  const navigateToLocation = (coordinates) => {
    setViewport(prev => ({
      ...prev,
      longitude: coordinates[0],
      latitude: coordinates[1],
      zoom: 16,
      transitionDuration: 1000,
    }));
  };

  // Navigate to selected location in 3D
  const navigateToLocation3D = (coordinates) => {
    setViewport(prev => ({
      ...prev,
      longitude: coordinates[0],
      latitude: coordinates[1],
      zoom: 18,
      pitch: is3DMode ? 60 : 0,
      bearing: 0,
      transitionDuration: 1000,
    }));
  };

  // Handle search input change with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInputValue) {
        searchAddress(searchInputValue);
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInputValue]);

  // Initialize Mapbox Draw
  useEffect(() => {
    if (!mapRef) return;

    const map = mapRef.getMap();

    // Create Mapbox Draw instance
    const drawInstance = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true,
        line_string: true,
        point: true,
      },
      styles: [
        {
          id: 'gl-draw-polygon-fill-inactive',
          type: 'fill',
          filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon']],
          paint: {
            'fill-color': '#3bb2d0',
            'fill-outline-color': '#3bb2d0',
            'fill-opacity': 0.1,
          },
        },
        {
          id: 'gl-draw-polygon-fill-active',
          type: 'fill',
          filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
          paint: {
            'fill-color': '#fbb03b',
            'fill-outline-color': '#fbb03b',
            'fill-opacity': 0.1,
          },
        },
        {
          id: 'gl-draw-polygon-stroke-inactive',
          type: 'line',
          filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon']],
          paint: {
            'line-color': '#3bb2d0',
            'line-width': 2,
          },
        },
        {
          id: 'gl-draw-polygon-stroke-active',
          type: 'line',
          filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
          paint: {
            'line-color': '#fbb03b',
            'line-width': 2,
          },
        },
        {
          id: 'gl-draw-line-inactive',
          type: 'line',
          filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'LineString']],
          paint: {
            'line-color': '#3bb2d0',
            'line-width': 2,
          },
        },
        {
          id: 'gl-draw-line-active',
          type: 'line',
          filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'LineString']],
          paint: {
            'line-color': '#fbb03b',
            'line-width': 2,
          },
        },
      ],
    });

    map.addControl(drawInstance);
    setDraw(drawInstance);

    // Event listeners
    map.on('draw.create', handleDrawCreate);
    map.on('draw.update', handleDrawUpdate);
    map.on('draw.delete', handleDrawDelete);
    map.on('draw.selectionchange', handleDrawSelectionChange);

    return () => {
      if (drawInstance) {
        map.removeControl(drawInstance);
      }
    };
  }, [mapRef]);

  const handleDrawCreate = useCallback((e) => {
    const features = e.features;
    features.forEach(feature => {
      const newPolygon = {
        id: `polygon-${Date.now()}`,
        type: 'Feature',
        geometry: feature.geometry,
        properties: {
          id: `polygon-${Date.now()}`, // Add id to properties for Mapbox expressions
          name: `Building ${polygons.length + 1}`,
          height: 20, // Default height for better 3D visibility
          base_height: 0,
          color: '#3bb2d0', // Default color
          type: 'building',
          created_at: new Date().toISOString(),
        },
      };
      setPolygons(prev => [...prev, newPolygon]);
      saveToUndoStack();
    });
  }, [polygons.length]);

  const handleDrawUpdate = useCallback((e) => {
    const features = e.features;
    features.forEach(feature => {
      setPolygons(prev =>
        prev.map(polygon =>
          polygon.id === feature.id ? { ...polygon, geometry: feature.geometry } : polygon
        )
      );
    });
    saveToUndoStack();
  }, []);

  const handleDrawDelete = useCallback((e) => {
    const features = e.features;
    features.forEach(feature => {
      setPolygons(prev => prev.filter(polygon => polygon.id !== feature.id));
    });
    saveToUndoStack();
  }, []);

  const handleDrawSelectionChange = useCallback((e) => {
    if (e.features.length > 0) {
      const feature = e.features[0];
      setSelectedFeature(feature);
      setEditingFeature(feature);
      setEditingProperties(feature.properties);
    } else {
      setSelectedFeature(null);
      setEditingFeature(null);
    }
  }, []);

  const saveToUndoStack = () => {
    const currentState = {
      polygons: [...polygons],
    };
    setUndoStack(prev => [...prev, currentState]);
    setRedoStack([]);
  };

  const handleUndo = () => {
    if (undoStack.length > 0) {
      const previousState = undoStack[undoStack.length - 1];
      const currentState = {
        polygons: [...polygons],
      };

      setRedoStack(prev => [...prev, currentState]);
      setUndoStack(prev => prev.slice(0, -1));

      setPolygons(previousState.polygons);
    }
  };

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const nextState = redoStack[redoStack.length - 1];
      const currentState = {
        polygons: [...polygons],
      };

      setUndoStack(prev => [...prev, currentState]);
      setRedoStack(prev => prev.slice(0, -1));

      setPolygons(nextState.polygons);
    }
  };

 

  const handleFeatureClick = (feature) => {
    setSelectedFeature(feature);
    
    // Show feedback that polygon is selected
    setSnackbar({ 
      open: true, 
      message: `Selected: ${feature.properties.name || 'Building'}`, 
      severity: 'info' 
    });
    
    // Navigate to the feature location
    if (feature.geometry.type === 'Polygon') {
      const coordinates = feature.geometry.coordinates[0];
      const center = coordinates.reduce(
        (acc, coord) => [acc[0] + coord[0], acc[1] + coord[1]],
        [0, 0]
      ).map(coord => coord / coordinates.length);
      
      if (is3DMode) {
        navigateToLocation3D(center);
      } else {
        setViewport(prev => ({
          ...prev,
          longitude: center[0],
          latitude: center[1],
          zoom: 18,
          transitionDuration: 1000,
        }));
      }
    }
  };

  const startEditing = (feature) => {
    setEditingFeature(feature);
    setEditingProperties({ 
      ...feature.properties,
      color: feature.properties.color || '#3bb2d0' // Ensure color is set
    });
  };

  const saveEditing = () => {
    if (editingFeature) {
      setPolygons(prev =>
        prev.map(polygon =>
          polygon.id === editingFeature.id
            ? { ...polygon, properties: { ...polygon.properties, ...editingProperties } }
            : polygon
        )
      );
      setSnackbar({ open: true, message: 'Properties saved successfully!', severity: 'success' });
    }
    setEditingFeature(null);
    setEditingProperties({});
  };

  const cancelEditing = () => {
    setEditingFeature(null);
    setEditingProperties({});
  };

  const handleDeleteFeature = (featureId) => {
    setPolygons(prev => prev.filter(polygon => polygon.id !== featureId));
    setSnackbar({ open: true, message: 'Building deleted successfully!', severity: 'success' });
  };

  const generateGeoJSON = () => {
    return {
      type: 'FeatureCollection',
      features: polygons,
      properties: {
        name: mapName || 'Stadium Map',
        created_at: new Date().toISOString(),
        total_buildings: polygons.length,
      },
    };
  };

  const generateGeoJSONWithPreview = () => {
    if (selectedFeature) {
      // Create updated features array with the previewed properties
      const updateFeature = (arr) =>
        arr.map((f) =>
          f.id === selectedFeature.id
            ? { ...f, properties: { ...f.properties, ...editingProperties } }
            : f
        );
      const previewPolygons = updateFeature(polygons);
      return {
        type: 'FeatureCollection',
        features: previewPolygons,
        properties: {
          name: mapName || 'Stadium Map',
          created_at: new Date().toISOString(),
          total_buildings: previewPolygons.length,
        },
      };
    }
    // Default: use saved state
    return generateGeoJSON();
  };

  const handleSaveMap = async () => {
    try {
      const geoJSON = generateGeoJSON();
      let response;
      if (selectedMap && selectedMap._id) {
        response = await updateMap(selectedMap._id, {
          name: mapName,
          geojson: geoJSON,
        });
      } else {

        response = await addMap({
          name: mapName || 'Stadium Map',
          geojson: geoJSON,
        });
      }

      if (response.data.success) {
        setSnackbar({ open: true, message: 'Map saved successfully!', severity: 'success' });
      } else {
        throw new Error(response.message || 'Failed to save map');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error saving map: ' + (error.message || error),
        severity: 'error'
      });
    }
  };

  const handleDeleteMap = () => {
    if (selectedMap && selectedMap._id) {
      setMapToDelete(selectedMap);
      setDeleteConfirmOpen(true);
    } else {
      setSnackbar({
        open: true,
        message: 'No map selected for deletion',
        severity: 'warning'
      });
    }
  };

  const confirmDeleteMap = async () => {
    try {
      if (!mapToDelete || !mapToDelete._id) {
        throw new Error('No map selected for deletion');
      }

      const response = await deleteMap(mapToDelete._id);
      
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Map deleted successfully!', severity: 'success' });
        
        // Remove the deleted map from the list
        setMapList(prev => prev.filter(map => map._id !== mapToDelete._id));
        
        // Reset to new map if the deleted map was selected
        if (selectedMap && selectedMap._id === mapToDelete._id) {
          setSelectedMap({ id: 'new', name: 'Create New Map' });
          setMapName('');
          setPolygons([]);
        }
      } else {
        throw new Error(response.data.message || 'Failed to delete map');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error deleting map: ' + (error.message || error),
        severity: 'error'
      });
    } finally {
      setDeleteConfirmOpen(false);
      setMapToDelete(null);
    }
  };

  const cancelDeleteMap = () => {
    setDeleteConfirmOpen(false);
    setMapToDelete(null);
  };

  const handleExportGeoJSON = () => {
    const geoJSON = generateGeoJSON();
    const dataStr = JSON.stringify(geoJSON, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${mapName || 'stadium-map'}.geojson`;
    link.click();
    URL.revokeObjectURL(url);
    setSnackbar({ open: true, message: 'GeoJSON exported successfully!', severity: 'success' });
  };

  // Copy GeoJSON to clipboard
  const handleCopyGeoJSON = () => {
    const geoJSON = generateGeoJSON();
    navigator.clipboard.writeText(JSON.stringify(geoJSON, null, 2));
    setSnackbar({ open: true, message: 'GeoJSON copied to clipboard!', severity: 'success' });
  };


  // 3D Polygon Layer Style
  const polygon3DLayerStyle = {
    id: 'polygons-3d-main',
    type: 'fill-extrusion',
    paint: {
      'fill-extrusion-color': [
        'case',
        ['has', 'color'],
        ['get', 'color'],
        '#3bb2d0'
      ],
      'fill-extrusion-opacity': 0.9,
      'fill-extrusion-height': ['get', 'height'],
      'fill-extrusion-base': ['get', 'base_height'],
      'fill-extrusion-translate': [0, 0],
      'fill-extrusion-translate-anchor': 'map',
      'fill-extrusion-ambient-occlusion': true,
      'fill-extrusion-ambient-occlusion-radius': 2,
      'fill-extrusion-ambient-occlusion-intensity': 0.3,
    },
    layout: {
      'visibility': 'visible'
    },
    interactive: true
  };

  // 2D Polygon Layer Style (for fallback and selection)
  const polygonLayerStyle = {
    id: 'polygons',
    type: 'fill',
    paint: {
      'fill-color': '#3bb2d0',
      'fill-opacity': 0.6,
      'fill-outline-color': '#3bb2d0',
    },
    interactive: true,
    layout: {
      'visibility': 'none' // Hide 2D when 3D is active
    }
  };

  const polygonBorderStyle = {
    id: 'polygons-border',
    type: 'line',
    paint: {
      'line-color': '#3bb2d0',
      'line-width': 2,
    },
  };

  const polygonLabelStyle = {
    id: 'polygon-labels',
    type: 'symbol',
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
      'text-size': 14,
      'text-anchor': 'center',
      'text-offset': [0, 0],
      'text-allow-overlap': true,
      'text-ignore-placement': false,
    },
    paint: {
      'text-color': '#ffffff',
      'text-halo-color': '#000000',
      'text-halo-width': 2,
      'text-halo-blur': 1,
    },
  };

  const selectedPolygonLabelStyle = {
    id: 'selected-polygon-label',
    type: 'symbol',
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
      'text-size': 16,
      'text-anchor': 'center',
      'text-offset': [0, 0],
      'text-allow-overlap': true,
      'text-ignore-placement': false,
    },
    paint: {
      'text-color': '#ff6b6b',
      'text-halo-color': '#ffffff',
      'text-halo-width': 3,
      'text-halo-blur': 1,
    },
  };

  // Fetch map list on mount
  useEffect(() => {
    const fetchMaps = async () => {
      try {
        const { data } = await getMaps();
        if (data.success && Array.isArray(data.data)) {
          setMapList(data.data);
        }
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to load maps', severity: 'error' });
      }
    };
    fetchMaps();
  }, []);

  // Load selected map
  useEffect(() => {
    const loadMap = async () => {
      if (selectedMap && selectedMap._id) {
        try {
          const { data } = await getMapById(selectedMap._id);
          if (data.success && data.data && data.data.geojson) {
            const geo = data.data.geojson;
            setMapName(geo.properties?.name || '');
            const loadedPolygons = geo.features
              .filter(f => f.properties.type === 'building')
              .map(feature => ({
                ...feature,
                properties: {
                  ...feature.properties,
                  id: feature.id || feature.properties.id || `polygon-${Date.now()}-${Math.random()}`
                }
              }));
            setPolygons(loadedPolygons);
            setUndoStack([]);
            setRedoStack([]);
          }
        } catch (err) {
          setSnackbar({ open: true, message: 'Failed to load map', severity: 'error' });
        }
      } else if (selectedMap && selectedMap.id === 'new') {
        // Reset for new map
        setMapName('');
        setPolygons([]);
        setUndoStack([]);
        setRedoStack([]);
      }
    };
    loadMap();
  }, [selectedMap]);

  // Debug: Monitor polygons changes
  useEffect(() => {
    if (polygons.length > 0 && is3DMode) {
      console.log('Polygons updated in 3D mode:', polygons.length, 'polygons');
      console.log('Sample polygon:', polygons[0]);
    }
  }, [polygons, is3DMode]);

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', position: 'relative' }}>
      {/* Map */}
      <Map
        ref={setMapRef}
        {...viewport}
        onMove={evt => setViewport(evt.viewState)}
        mapStyle={getCurrentMapStyle()}
        style={{ width: '100%', height: '100%' }}
        onClick={(e) => {
          const clickPoint = [e.lngLat.lng, e.lngLat.lat];
          let clickedPolygon = null;
          
          // Check each polygon to see if the click point is inside
          for (const polygon of polygons) {
            if (polygon.geometry.type === 'Polygon' && polygon.geometry.coordinates) {
              const coordinates = polygon.geometry.coordinates[0]; // Get the outer ring
              if (isPointInPolygon(clickPoint, coordinates)) {
                clickedPolygon = polygon;
                break;
              }
            }
          }
          
          if (clickedPolygon) {
            // Found a polygon, select it for editing
            handleFeatureClick(clickedPolygon);
          } else {
            // No polygon clicked, deselect current feature
            if (selectedFeature) {
              setSelectedFeature(null);
            }
          }
        }}
        onMouseMove={(e) => {
          if (!mapRef) return;
          
          const map = mapRef.getMap();
          const mousePoint = [e.lngLat.lng, e.lngLat.lat];
          let isOverPolygon = false;
          
          // Check each polygon to see if the mouse is over it
          for (const polygon of polygons) {
            if (polygon.geometry.type === 'Polygon' && polygon.geometry.coordinates) {
              const coordinates = polygon.geometry.coordinates[0]; // Get the outer ring
              if (isPointInPolygon(mousePoint, coordinates)) {
                isOverPolygon = true;
                break;
              }
            }
          }
          
          // Change cursor based on whether we're over a polygon
          map.getCanvas().style.cursor = isOverPolygon ? 'pointer' : 'grab';
        }}
      >
        <NavigationControl position="top-right" />
        <FullscreenControl position="top-right" />

        {/* Polygon Layers */}
        {polygons.length > 0 && (
          <>
                    {/* 3D Polygon Layer - All Polygons */}
        {is3DMode && polygons.length > 0 && (
          <Source type="geojson" data={{ type: 'FeatureCollection', features: polygons }}>
            <Layer 
              {...polygon3DLayerStyle}
              onClick={(e) => {
                if (e.features && e.features.length > 0) {
                  const feature = e.features[0];
                  handleFeatureClick(feature);
                }
              }}
            />
          </Source>
        )}
            
            {/* 2D Polygon Layer - All Polygons */}
            {!is3DMode && (
              <Source type="geojson" data={{ type: 'FeatureCollection', features: polygons }}>
                <Layer {...polygonLayerStyle} />
                <Layer {...polygonBorderStyle} />
              </Source>
            )}
            
            {/* Label Layer */}
            {showLabels && (
              <Source type="geojson" data={{ type: 'FeatureCollection', features: generateLabelFeatures() }}>
                <Layer {...polygonLabelStyle} />
              </Source>
            )}
          </>
        )}

        {/* Selected Polygon Highlight Layer - Only for 2D mode */}
        {selectedFeature && !is3DMode && (
          <Source type="geojson" data={{ type: 'FeatureCollection', features: [selectedFeature] }}>
            <Layer
              id="selected-polygon"
              type="fill"
              paint={{
                'fill-color': '#ff6b6b',
                'fill-opacity': 0.8,
                'fill-outline-color': '#ff6b6b',
              }}
            />
            <Layer
              id="selected-polygon-border"
              type="line"
              paint={{
                'line-color': '#ff6b6b',
                'line-width': 4,
              }}
            />
          </Source>
        )}

        {/* Selected Polygon Highlight Layer - Only for 3D mode */}
        {selectedFeature && is3DMode && (
          <Source type="geojson" data={{ type: 'FeatureCollection', features: [selectedFeature] }}>
            <Layer
              id="selected-polygon-3d-highlight"
              type="fill-extrusion"
              paint={{
                'fill-extrusion-color': '#ff6b6b',
                'fill-extrusion-opacity': 0.95,
                'fill-extrusion-height': ['get', 'height'],
                'fill-extrusion-base': ['get', 'base_height'],
                'fill-extrusion-translate': [0, 0],
                'fill-extrusion-translate-anchor': 'map',
                'fill-extrusion-ambient-occlusion': true,
                'fill-extrusion-ambient-occlusion-radius': 2,
                'fill-extrusion-ambient-occlusion-intensity': 0.3,
              }}
            />
          </Source>
        )}

        {/* Selected Polygon Label */}
        {selectedFeature && showLabels && (
          <Source type="geojson" data={{ 
            type: 'FeatureCollection', 
            features: [{
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: calculatePolygonCenter(selectedFeature.geometry.coordinates),
              },
              properties: {
                name: selectedFeature.properties.name || 'Building',
                id: selectedFeature.id,
              },
            }]
          }}>
            <Layer {...selectedPolygonLabelStyle} />
          </Source>
        )}


      </Map>

      {/* Control Panel */}
      <Paper
        sx={{
          position: 'absolute',
          top: 20,
          left: 20,
          p: 2,
          zIndex: 1000,
          minWidth: 300,
        }}
      >
        {/* Map Selection */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete
              options={[{ id: 'new', name: 'Create New Map' }, ...mapList]}
              getOptionLabel={option => option.name}
              value={selectedMap}
              onChange={(e, newValue) => setSelectedMap(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select Map" size="small" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ flexGrow: 1 }}
            />
          </Box>
        </Box>

        {/* Search Field */}
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            freeSolo
            options={searchResults}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.name
            }
            inputValue={searchInputValue}
            onInputChange={(event, newInputValue) => {
              setSearchInputValue(newInputValue);
            }}
            onChange={(event, newValue) => {
              if (newValue && newValue.coordinates) {
                navigateToLocation(newValue.coordinates);
                setSearchInputValue(newValue.name);
                setSearchResults([]);
              }
            }}
            loading={isSearching}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Address"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                placeholder="Enter address to search..."
              />
            )}
            renderOption={(props, option) => (
              <ListItem {...props}>
                <ListItemIcon>
                  <LocationIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={option.name}
                  secondary={option.type}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            )}
            noOptionsText={searchInputValue ? "No locations found" : "Type to search..."}
            sx={{ mb: 2 }}
          />
        </Box>

        {/* Map Style Selector */}
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Map Style</InputLabel>
            <Select
              value={currentMapStyle}
              label="Map Style"
              onChange={(e) => setCurrentMapStyle(e.target.value)}
            >
              {mapStyles.map((style) => (
                <MenuItem key={style.id} value={style.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {style.icon}
                    <Box>
                      <Typography variant="body2">{style.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {style.description}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TextField
          fullWidth
          label="Map Name"
          value={mapName}
          onChange={(e) => setMapName(e.target.value)}
          margin="normal"
          size="small"
        />

  

        <Divider sx={{ my: 2 }} />

     

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<UndoIcon />}
            onClick={handleUndo}
            disabled={undoStack.length === 0}
          >
            Undo
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RedoIcon />}
            onClick={handleRedo}
            disabled={redoStack.length === 0}
          >
            Redo
          </Button>
          <Button
            variant={showLabels ? "contained" : "outlined"}
            size="small"
            startIcon={showLabels ? <LabelIcon /> : <LabelOffIcon />}
            onClick={() => setShowLabels(!showLabels)}
          >
            {showLabels ? 'Hide Labels' : 'Show Labels'}
          </Button>
          <Button
            variant={is3DMode ? "contained" : "outlined"}
            size="small"
            startIcon={is3DMode ? <ViewInArIcon /> : <ViewModuleIcon />}
            onClick={toggle3DMode}
          >
            {is3DMode ? '3D View' : '2D View'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CameraAltIcon />}
            onClick={resetCamera}
          >
            Reset Camera
          </Button>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSaveMap}
          >
            Save Map
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleExportGeoJSON}
          >
            Export GeoJSON
          </Button>
          {selectedMap && selectedMap._id && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteMap}
            >
              Delete Map
            </Button>
          )}
        </Box>

        {/* GeoJSON Panel */}
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Current GeoJSON</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ flexGrow: 1 }}>
                This is the current GeoJSON for your map.
              </Typography>
              <IconButton size="small" onClick={handleCopyGeoJSON}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box
              component="pre"
              sx={{
                background: '#f5f5f5',
                p: 1,
                borderRadius: 1,
                maxHeight: 250,
                overflow: 'auto',
                fontSize: 12,
                fontFamily: 'monospace',
              }}
            >
              {JSON.stringify(generateGeoJSONWithPreview(), null, 2)}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Features List Drawer */}
      <Fab
        color="primary"
        aria-label="features"
        sx={{ position: 'absolute', bottom: 20, right: 20 }}
        onClick={() => setDrawerOpen(true)}
      >
        <MapIcon />
      </Fab>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: 400 } }}
      >
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Features
            </Typography>
            <IconButton
              color="inherit"
              onClick={() => setDrawerOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 2, overflow: 'auto', maxHeight: 'calc(100vh - 64px)' }}>
          <Typography variant="h6" gutterBottom>
            Buildings ({polygons.length})
          </Typography>
          <List dense>
            {polygons.map((polygon) => (
              <Card 
                key={polygon.id} 
                sx={{ 
                  mb: 1, 
                  border: selectedFeature?.id === polygon.id ? '2px solid #ff6b6b' : 
                          editingFeature?.id === polygon.id ? '2px solid #ffd93d' : '1px solid #e0e0e0',
                  backgroundColor: selectedFeature?.id === polygon.id ? '#fff5f5' :
                                  editingFeature?.id === polygon.id ? '#fffbf0' : 'white'
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {editingFeature?.id === polygon.id ? (
                    // Inline editing mode
                    <Box>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Name"
                            value={editingProperties.name || ''}
                            onChange={(e) => setEditingProperties(prev => ({ ...prev, name: e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Height (m)"
                            type="number"
                            value={editingProperties.height || 0}
                            onChange={(e) => setEditingProperties(prev => ({ ...prev, height: parseFloat(e.target.value) || 0 }))}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Base Height (m)"
                            type="number"
                            value={editingProperties.base_height || 0}
                            onChange={(e) => setEditingProperties(prev => ({ ...prev, base_height: parseFloat(e.target.value) || 0 }))}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Color"
                            value={editingProperties.color || '#3bb2d0'}
                            onChange={(e) => setEditingProperties(prev => ({ ...prev, color: e.target.value }))}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Box
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: 1,
                                      backgroundColor: editingProperties.color || '#3bb2d0',
                                      border: '1px solid #ccc',
                                    }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<CheckIcon />}
                              onClick={saveEditing}
                            >
                              Save
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CancelIcon />}
                              onClick={cancelEditing}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  ) : (
                    // View mode
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flexGrow: 1, cursor: 'pointer' }} onClick={() => handleFeatureClick(polygon)}>
                          <Typography variant="subtitle2" gutterBottom>
                            {polygon.properties.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Height: {polygon.properties.height}m | Base: {polygon.properties.base_height}m
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Color:
                            </Typography>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: 0.5,
                                backgroundColor: polygon.properties.color || '#3bb2d0',
                                border: '1px solid #ccc',
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => startEditing(polygon)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteFeature(polygon.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={cancelDeleteMap} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Delete Map
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the map "{mapToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteMap} variant="outlined">
            Cancel
          </Button>
          <Button onClick={confirmDeleteMap} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MapEditor; 